"use client";
import { Fragment, jsx, jsxs } from "react/jsx-runtime";
import { useCallback, useMemo, useState } from "react";
import Clock from "@crehana/compass.icons.outline/Clock";
import { cn } from "../utils.js";
import { Popper, PopperContent, PopperTrigger } from "../Popper/index.js";
import { TimePickerColumn } from "./time-picker-column.js";
import { calculateTotalMinutes, formatTimeDisplay, generateMinuteArray, parseTimeValue } from "./utils.js";
const AMPM_OPTIONS = [
    'a.m',
    'p.m'
];
const TimePickerColumns = ({ className, value, mode = 'a.m/p.m', placeholder, onTimeChange, minuteInterval = 1, disabled, removePortal, size = 's', error, helperText })=>{
    const [open, setOpen] = useState(false);
    const hours = useMemo(()=>{
        if ('a.m/p.m' === mode) return Array.from({
            length: 12
        }, (_, i)=>{
            const h = 0 === i ? 12 : i;
            return ('0' + h).slice(-2);
        });
        return Array.from({
            length: 24
        }, (_, i)=>('0' + i).slice(-2));
    }, [
        mode
    ]);
    const minutes = useMemo(()=>generateMinuteArray(minuteInterval), [
        minuteInterval
    ]);
    const parsed = useMemo(()=>value ? parseTimeValue(value) : void 0, [
        value
    ]);
    const selectedHour = useMemo(()=>{
        if (!parsed) return '';
        if ('a.m/p.m' === mode) {
            const h12 = parsed.hour % 12 || 12;
            return ('0' + h12).slice(-2);
        }
        return ('0' + parsed.hour).slice(-2);
    }, [
        parsed,
        mode
    ]);
    const selectedMinute = useMemo(()=>{
        if (!parsed) return '';
        return ('0' + parsed.minute).slice(-2);
    }, [
        parsed
    ]);
    const selectedAmPm = useMemo(()=>{
        if (!parsed || 'a.m/p.m' !== mode) return '';
        return parsed.hour >= 12 ? 'p.m' : 'a.m';
    }, [
        parsed,
        mode
    ]);
    const buildValue = useCallback((hour, minute, amPm)=>{
        if (!hour || !minute) return;
        let h = parseInt(hour);
        if ('a.m/p.m' === mode) {
            if ('p.m' === amPm && 12 !== h) h += 12;
            if ('a.m' === amPm && 12 === h) h = 0;
        }
        return `${('0' + h).slice(-2)}:${minute}`;
    }, [
        mode
    ]);
    const emitTime = useCallback((hour, minute, amPm)=>{
        const resolvedHour = hour || ('a.m/p.m' === mode ? '12' : '00');
        const resolvedMinute = minute || '00';
        const resolvedAmPm = amPm || ('a.m/p.m' === mode ? 'a.m' : '');
        const newValue = buildValue(resolvedHour, resolvedMinute, resolvedAmPm);
        if (newValue) {
            const [h, m] = newValue.split(':').map(Number);
            onTimeChange?.(newValue, calculateTotalMinutes(h, m));
        }
    }, [
        mode,
        buildValue,
        onTimeChange
    ]);
    const handleSelectHour = useCallback((hour)=>{
        emitTime(hour, selectedMinute, selectedAmPm);
    }, [
        emitTime,
        selectedMinute,
        selectedAmPm
    ]);
    const handleSelectMinute = useCallback((minute)=>{
        emitTime(selectedHour, minute, selectedAmPm);
    }, [
        emitTime,
        selectedHour,
        selectedAmPm
    ]);
    const handleSelectAmPm = useCallback((amPm)=>{
        emitTime(selectedHour, selectedMinute, amPm);
    }, [
        emitTime,
        selectedHour,
        selectedMinute
    ]);
    const displayValue = useMemo(()=>value ? formatTimeDisplay(value, mode) : '', [
        value,
        mode
    ]);
    return /*#__PURE__*/ jsxs("div", {
        className: "tw-flex tw-flex-col tw-gap-0.8",
        children: [
            /*#__PURE__*/ jsxs(Popper, {
                open: open,
                onOpenChange: setOpen,
                children: [
                    /*#__PURE__*/ jsx(PopperTrigger, {
                        asChild: true,
                        disabled: disabled,
                        children: /*#__PURE__*/ jsxs("button", {
                            type: "button",
                            disabled: disabled,
                            "aria-invalid": error || void 0,
                            className: cn('tw-group tw-cursor-pointer tw-justify-between tw-input tw-flex tw-items-center', 'enabled:data-[state=closed]:tw-border-neutral-light-600 enabled:data-[state=closed]:tw-ring-neutral-light-600', 'm' === size && 'tw-h-5.6 tw-font-body3 tw-text-1.6', error && 'tw-border-alert-400 hover:tw-border-alert-400 focus-within:tw-border-alert-400 aria-expanded:tw-border-alert-400', disabled && 'tw-opacity-50 tw-pointer-events-none', className),
                            children: [
                                /*#__PURE__*/ jsx("span", {
                                    className: "tw-line-clamp-1 tw-grow tw-text-left",
                                    children: displayValue ? displayValue : /*#__PURE__*/ jsx("span", {
                                        className: "tw-text-neutral-light-700",
                                        children: placeholder
                                    })
                                }),
                                /*#__PURE__*/ jsx("div", {
                                    className: "tw-flex tw-items-center tw-shrink-0",
                                    children: /*#__PURE__*/ jsx(Clock, {
                                        className: cn(error ? 'tw-text-alert-400' : 'tw-text-neutral-light-600'),
                                        size: 24
                                    })
                                })
                            ]
                        })
                    }),
                    /*#__PURE__*/ jsx(PopperContent, {
                        removePortal: removePortal,
                        removePadding: true,
                        align: "start",
                        className: "tw-overflow-hidden",
                        children: /*#__PURE__*/ jsxs("div", {
                            className: "tw-flex",
                            children: [
                                /*#__PURE__*/ jsx(TimePickerColumn, {
                                    items: hours,
                                    selectedValue: selectedHour,
                                    onSelect: handleSelectHour,
                                    label: "Hour"
                                }),
                                /*#__PURE__*/ jsx("div", {
                                    className: "tw-w-px tw-bg-neutral-light-400"
                                }),
                                /*#__PURE__*/ jsx(TimePickerColumn, {
                                    items: minutes,
                                    selectedValue: selectedMinute,
                                    onSelect: handleSelectMinute,
                                    label: "Minute"
                                }),
                                'a.m/p.m' === mode && /*#__PURE__*/ jsxs(Fragment, {
                                    children: [
                                        /*#__PURE__*/ jsx("div", {
                                            className: "tw-w-px tw-bg-neutral-light-400"
                                        }),
                                        /*#__PURE__*/ jsx(TimePickerColumn, {
                                            items: AMPM_OPTIONS,
                                            selectedValue: selectedAmPm,
                                            onSelect: handleSelectAmPm,
                                            label: "a.m/p.m"
                                        })
                                    ]
                                })
                            ]
                        })
                    })
                ]
            }),
            error && helperText && /*#__PURE__*/ jsx("p", {
                className: "tw-font-caption tw-text-alert-400",
                children: helperText
            })
        ]
    });
};
export { TimePickerColumns };
